'use client';
import { InnerContainer } from '../../ui/common/inner-container';
import { MouseEvent, useMemo, useState } from 'react';
import ImageWithFallback from '../../ui/image/image-with-fallback';
import fmt from '@/src/utils/helpers/number-format';
import { CurrencyTypeMissing } from '@/src/interfaces/missing-types';
import { usePathname } from 'next/navigation';
import ShowMoreBtn from '../../ui/show-more-btn/show-more-btn';
import { ResponseInterestAndProfitRateTable } from '@/src/interfaces/data-contracts';
import CSRLink from '../../ui/csrlink';

type Props = {
  data: ResponseInterestAndProfitRateTable;
  title?: string;
  type?: string;
  linkPathname?: string;
};

function InterestTable({ data, title, type, linkPathname }: Readonly<Props>) {
  const pathname = usePathname();
  const [showAll, setShowAll] = useState(false);
  const date = useMemo(() => {
    return new Intl.DateTimeFormat('tr-TR', {
      dateStyle: 'long',
    }).format(new Date());
  }, []);

  return (
    <div>
      <InnerContainer>
        <h2 className="mb-[24px] text-[20px] font-semibold leading-[28px] text-[#252525]  md:mb-[32px] md:text-[24px] md:leading-[32px]">
          {title} / Finansmanı Faiz ve Kâr Payı Oranları - {date}
        </h2>
      </InnerContainer>
      <div className="max-w-full overflow-scroll md:m-auto md:max-w-[1120px] md:overflow-auto">
        <table className="min-w-[1120px] text-center md:min-w-full">
          <thead>
            <tr className="h-[40px] bg-[#F5F5F5] text-[14px] leading-[20px] text-[#171717]">
              <th className="w-[200px] border-r-[1px] border-[#fff]">Bankalar</th>
              <th className="w-[298px] border-r-[1px] border-[#fff]">Kredi / Finansman Tutarı (min - max)</th>
              <th className="w-[298px] border-r-[1px] border-[#fff]">Vade (min - max)</th>
              <th className="w-[298px] border-r-[1px] border-[#fff]">Faiz/Kâr Payı (min - max)</th>
              <th className="w-[100px]"></th>
            </tr>
          </thead>
          <tbody>
            {data.responseRateAndProfits?.map((item, i) => (
              <tr
                key={i}
                className={`h-[56px] max-h-[56px] text-[14px] leading-[20px] text-[#454545] even:bg-[#FAFAFA] ${!showAll && i === 5 ? 'opacity-25' : ''} ${!showAll && i > 5 ? 'hidden' : ''}`}
              >
                <td className="flex h-[56px] items-center justify-center">
                  <CSRLink
                    title={`${item.bankName} ${type ?? title}`}
                    href={`${linkPathname ?? pathname}/${item.bankFriendlyUrl}`}
                    rel="noopener"
                    onClick={
                      !showAll && i === 5
                        ? (e: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) => {
                          e.preventDefault();
                        }
                        : undefined
                    }
                    className={!showAll && i === 5 ? 'cursor-default' : ''}
                    event={{
                      event: 'GAEvent',
                      eventCategory: 'Button Click',
                      eventAction: `${type ?? title} – Faiz ve Kar Payi Oranlari`,
                      eventLabel: `${item.bankName} - ${item.bankName}`,
                    }}
                  >
                    <ImageWithFallback
                      className={'h-10 max-h-10'}
                      width={85}
                      height={40}
                      src={item.bankLogo}
                      alt={item.bankName ?? 'bank name'}
                      data-testid="bankLogo"
                    />
                  </CSRLink>
                </td>
                <td>
                  {fmt(item.minimumAmount, CurrencyTypeMissing.TRY, false, false, 0)} -{' '}
                  {fmt(item.maximumAmount, CurrencyTypeMissing.TRY, false, false, 0)} TL
                </td>
                <td>
                  {item.minimumMaturity} - {item.maximumMaturity} Ay
                </td>
                <td>
                  % {item.minimumRate?.toFixed(2).toString().replace('.', ',')} -{' '}
                  {item.maximumRate?.toFixed(2).toString().replace('.', ',')}
                </td>
                <td>
                  <CSRLink
                    href={`${linkPathname ?? pathname}/${item.bankFriendlyUrl}`}
                    rel="nofollow noopener"
                    onClick={
                      !showAll && i === 5
                        ? (e: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) => {
                          e.preventDefault();
                        }
                        : undefined
                    }
                    className={!showAll && i === 5 ? 'cursor-default' : ''}
                    event={{
                      event: 'GAEvent',
                      eventCategory: 'Button Click',
                      eventAction: `${type ?? title} – Faiz ve Kar Payi Oranlari`,
                      eventLabel: `${item.bankName} - Incele`,
                    }}
                  >
                    <span className="text-[#4A49BB]">İncele</span>
                  </CSRLink>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <InnerContainer>
        <p className="my-4 text-[14px] leading-[20px] text-[#3D3D3D]">
          {date} tarihinde yaptığımız son değerlendirmeye göre 24 ay vadeli 200.000 TL KOBİ Kredisi için en avantajlı
          teklifi sunan banka %{data.lowestInterestRate?.toFixed(2)} Faiz oranı ile {data.bankNames?.join(', ')} oldu.
        </p>
        {data.responseRateAndProfits && data.responseRateAndProfits.length > 5 && (
          <ShowMoreBtn
            showMoreText="Daha Fazla Gör"
            showLessText="Daha Az Gör"
            onShowState={() => {
              setShowAll((prev) => !prev);
            }}
            data-testid="showMoreButton"
          />
        )}
      </InnerContainer>
    </div>
  );
}

export default InterestTable;
